import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Today as TodayIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface EngagementMetricsProps {
  days?: number;
  onDaysChange?: (days: number) => void;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
}

function StatCard({ title, value, icon, color = 'primary', subtitle }: StatCardProps) {
  const getColor = (color: string) => {
    const colors = {
      primary: '#1976d2',
      secondary: '#dc004e',
      success: '#2e7d32',
      warning: '#ed6c02',
      error: '#d32f2f',
      info: '#0288d1',
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: getColor(color) + '15',
              color: getColor(color),
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 600 }}>
          {value.toLocaleString()}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function EngagementMetrics({ days = 30, onDaysChange }: EngagementMetricsProps) {
  const [includeBlocked, setIncludeBlocked] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['engagementMetrics', days, includeBlocked],
    queryFn: () => statsApi.getEngagementMetrics(days, includeBlocked),
    refetchInterval: 60000, // Обновляем каждую минуту
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки engagement метрик: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Нет данных для отображения
      </Alert>
    );
  }

  const { current_metrics, dau_trend, wau_trend } = data;

  return (
    <Box>
      {/* Period and Filter Selectors */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {/* Blocked Users Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={includeBlocked}
              onChange={(e) => setIncludeBlocked(e.target.checked)}
              color="primary"
            />
          }
          label="Считать заблокированных пользователей"
        />

        {/* Period Selector */}
        {onDaysChange && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Период</InputLabel>
            <Select
              value={days}
              label="Период"
              onChange={(e) => onDaysChange(e.target.value as number)}
            >
              <MenuItem value={7}>7 дней</MenuItem>
              <MenuItem value={14}>14 дней</MenuItem>
              <MenuItem value={30}>30 дней</MenuItem>
              <MenuItem value={90}>90 дней</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Main Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего активных"
            value={current_metrics.active_users_all_time}
            icon={<PeopleIcon />}
            color="primary"
            subtitle={`${current_metrics.engagement_rate_all_time}% от всех пользователей`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="DAU"
            value={current_metrics.dau}
            icon={<TodayIcon />}
            color="success"
            subtitle={`${current_metrics.engagement_rate_dau}% активны сегодня`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="WAU"
            value={current_metrics.wau}
            icon={<CalendarTodayIcon />}
            color="info"
            subtitle={`${current_metrics.engagement_rate_wau}% активны за неделю`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="MAU"
            value={current_metrics.mau}
            icon={<DateRangeIcon />}
            color="warning"
            subtitle={`${current_metrics.engagement_rate_mau}% активны за месяц`}
          />
        </Grid>
      </Grid>

      {/* Additional Info Card */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Сводка по вовлеченности
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Всего зарегистрировано: {' '}
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {current_metrics.total_registered_users.toLocaleString()}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Хотя бы раз использовали: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="primary.main">
                    {current_metrics.active_users_all_time.toLocaleString()} ({current_metrics.engagement_rate_all_time}%)
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Активных в последний месяц: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="warning.main">
                    {current_metrics.mau.toLocaleString()}
                  </Typography>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Engagement Rate
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  DAU: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="success.main">
                    {current_metrics.engagement_rate_dau}%
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  WAU: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="info.main">
                    {current_metrics.engagement_rate_wau}%
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  MAU: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="warning.main">
                    {current_metrics.engagement_rate_mau}%
                  </Typography>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DAU Trend Chart */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Динамика активных пользователей (DAU)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dau_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="active_users"
                stroke="#2e7d32"
                name="Активные пользователи"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* WAU Trend Chart */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Динамика активных пользователей по неделям (WAU)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={wau_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="active_users"
                stroke="#0288d1"
                name="Активные пользователи"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
